/**
 * Units a recipe line is likely to be counted by, offered as suggestions.
 *
 * Two lists, and one rule: a unit is only ever pluralised if it is in
 * `UNIT_PLURALS`. Anything else renders exactly as typed, so a unit nobody
 * anticipated ("2 handfuls of breadcrumbs") can never come out mangled.
 */

/**
 * Countable units, canonical lowercase singular -> plural.
 *
 * The plurals are written out rather than derived, because "add an s" is wrong
 * for the -ch/-sh/-s endings and for leaf. Keeping it a lookup also means we
 * only need to be right about units we actually suggest.
 *
 * Measures that do not take a plural (g, ml, tsp, Tbsp, cup, fl oz) are
 * deliberately absent: cookbooks write "2 tbsp", not "2 tsps".
 */
export const UNIT_PLURALS: Record<string, string> = {
  piece: 'pieces',
  can: 'cans',
  tin: 'tins',
  jar: 'jars',
  bottle: 'bottles',
  packet: 'packets',
  box: 'boxes',
  bag: 'bags',
  clove: 'cloves',
  slice: 'slices',
  bunch: 'bunches',
  sprig: 'sprigs',
  head: 'heads',
  stalk: 'stalks',
  stick: 'sticks',
  leaf: 'leaves',
  handful: 'handfuls',
  pinch: 'pinches',
  dash: 'dashes',
}

const PLURAL_TO_SINGULAR: Record<string, string> = Object.fromEntries(
  Object.entries(UNIT_PLURALS).map(([singular, plural]) => [plural, singular]),
)

/**
 * Suggestions for the unit field: the countable vocabulary above, plus the
 * measures that stand alone. Written the way they would actually be written,
 * so capitalisation is right even though storage is lowercase.
 */
export const COMMON_UNITS = [
  'g',
  'kg',
  'ml',
  'l',
  'tsp',
  'Tbsp',
  'cup',
  'fl oz',
  'to taste',
  ...Object.keys(UNIT_PLURALS),
]

/** Lowercased and singular, so "Cans", "cans" and "can" all compare equal. */
export function normalizeUnit(unit: string | null | undefined): string {
  const key = unit?.trim().toLowerCase() ?? ''
  return PLURAL_TO_SINGULAR[key] ?? key
}

/**
 * The unit as it should read for this amount: plural above one, left alone at
 * one or below. Returns the input untouched when the unit is not one we know.
 */
export function pluralizeUnit(
  unit: string | null | undefined,
  amount: number | null | undefined,
): string {
  const trimmed = unit?.trim() ?? ''
  if (!trimmed || amount === null || amount === undefined || amount <= 1) return trimmed
  const plural = UNIT_PLURALS[normalizeUnit(trimmed)]
  if (!plural) return trimmed
  // Match the capitalisation the user typed, so "Can" still reads "Cans".
  return /^[A-Z]/.test(trimmed) ? plural.charAt(0).toUpperCase() + plural.slice(1) : plural
}
